<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

const DISMISS_KEY = 'nexceo_pwa_install_dismissed';

const visible = ref(false);
const showIosTip = ref(false);
let deferredPrompt = null;

function isDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return true;
  }
}

function dismiss() {
  visible.value = false;
  showIosTip.value = false;
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // ignore
  }
}

async function install() {
  if (!deferredPrompt) return;
  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  if (outcome === 'accepted') visible.value = false;
}

function onBeforeInstallPrompt(event) {
  event.preventDefault();
  deferredPrompt = event;
  window.nexceoPwaPrompt = event;
  if (!isDismissed()) visible.value = true;
}

function onAppInstalled() {
  visible.value = false;
  showIosTip.value = false;
  deferredPrompt = null;
  window.nexceoPwaPrompt = null;
  window.nexceoPwaInstalled = true;
}

onMounted(() => {
  if (isDismissed()) return;

  // Pick up a prompt already captured in the layout script before Vue mounted
  if (window.nexceoPwaPrompt) {
    deferredPrompt = window.nexceoPwaPrompt;
    visible.value = true;
    return;
  }

  // iOS Safari: no beforeinstallprompt — show manual "Share → Add to Home Screen" tip
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  if (isIos && !isStandalone) {
    showIosTip.value = true;
    return;
  }

  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
});

onUnmounted(() => {
  window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.removeEventListener('appinstalled', onAppInstalled);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <!-- Android / Desktop install banner -->
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-2"
  >
    <div
      v-if="visible"
      class="fixed bottom-4 right-4 z-50 flex items-center gap-3 bg-n-slate-12 text-white rounded-xl shadow-lg px-4 py-3 max-w-xs"
      role="status"
      aria-live="polite"
    >
      <span class="i-lucide-smartphone size-4 shrink-0 text-woot-400" />
      <p class="text-sm font-medium leading-snug flex-1">Install NEXCEO App</p>
      <button
        class="text-sm font-semibold text-woot-400 hover:text-woot-300 transition-colors shrink-0"
        @click="install"
      >
        Install
      </button>
      <button
        class="ml-1 text-n-slate-7 hover:text-n-slate-5 transition-colors shrink-0"
        aria-label="Dismiss install prompt"
        @click="dismiss"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>
  </Transition>

  <!-- iOS Safari: manual install tip (no beforeinstallprompt available) -->
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-2"
  >
    <div
      v-if="showIosTip"
      class="fixed bottom-4 right-4 z-50 flex items-start gap-3 bg-n-slate-12 text-white rounded-xl shadow-lg px-4 py-3 max-w-xs"
      role="status"
      aria-live="polite"
    >
      <span class="i-lucide-share size-4 shrink-0 text-woot-400 mt-0.5" />
      <p class="text-sm leading-snug flex-1">
        <span class="font-semibold">Add to Home Screen:</span>
        tap Share then <em>"Add to Home Screen"</em>
      </p>
      <button
        class="ml-1 text-n-slate-7 hover:text-n-slate-5 transition-colors shrink-0"
        aria-label="Dismiss"
        @click="dismiss"
      >
        <span class="i-lucide-x size-4" />
      </button>
    </div>
  </Transition>
</template>
